import React, { Component } from "react";
import { PropTypes } from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import classNames from "classnames";
import { hasEntitlement } from "@zendesk/knowledge-context";
import { Avatar, Pagination, Ellipsis } from "@zendesk/self-service-components";
import { Spinner } from "@zendeskgarden/react-loaders";
import t from "@zendesk/client-i18n-tools";
import * as actions from "../../../../actions/userSegment";
import { STAFF, SIGNED_IN_USERS } from "../../../../constants";
import styles from "./index.css";

export class UserList extends Component {
  constructor(props) {
    super(props);

    this.state = {
      // The pagination state is kept in the component, because parameters by which
      // users are filtered (tags, orgs, groups) can be changed without it being reflected in
      // the URL, making the page parameter there useless (it won't produce the same result)
      page: 1,
    };

    this.onPageSelected = this.onPageSelected.bind(this);
  }

  onPageSelected(page) {
    const nextPageNumber = page + 1;
    const { fetchUsers } = this.props;

    this.setState({ page: nextPageNumber });
    fetchUsers(nextPageNumber);
  }

  renderRows() {
    const { users, isFetching, userType } = this.props;

    if (isFetching) {
      return (
        <tr className={styles["c-table__row"]}>
          <td
            colSpan={4}
            className={classNames(styles["c-table__row__cell"], styles.cell)}
          >
            <div className={styles.loader} data-test-id="user-list-loader">
              <Spinner />
            </div>
          </td>
        </tr>
      );
    }

    if (users.length === 0) {
      return (
        <tr className={styles["c-table__row"]}>
          <td
            colSpan={3}
            className={classNames(styles["c-table__row__cell"], styles.cell)}
            data-test-id="user-list-empty"
          >
            {t("knowledge-user-segments.userSegment.noUsers")}
          </td>
        </tr>
      );
    }

    return users.map(user => {
      const userTypeSpecificProperty =
        userType === STAFF ? user.groups : user.organizations;

      return (
        <tr
          key={user.id}
          className={styles["c-table__row"]}
          data-test-id="user-row"
        >
          <td
            className={classNames(styles["c-table__row__cell"], styles.cell)}
            data-test-id="user-name"
          >
            <a
              data-test-id="user-profile-in-support-link"
              target="_blank"
              href={`/agent/users/${user.id}`}
              title={t("knowledge-user-segments.userSegment.profileInSupport", {
                name: user.name,
              })}
            >
              <Avatar
                key="avatar"
                className={styles.avatar}
                size="medium"
                alt={user.name}
                src={user.photo.content_url}
              />
              <span className={styles.name}>{user.name}</span>
            </a>
          </td>
          <td
            className={classNames(styles["c-table__row__cell"], styles.cell)}
            data-test-id="user-email"
          >
            <Ellipsis>{user.email}</Ellipsis>
          </td>
          <td
            className={classNames(styles["c-table__row__cell"], styles.cell)}
            data-test-id="user-tags"
          >
            <Ellipsis>{user.tags.join(", ")}</Ellipsis>
          </td>

          <td
            className={classNames(styles["c-table__row__cell"], styles.cell)}
            data-test-id="user-groups-orgs"
          >
            <Ellipsis>
              {userTypeSpecificProperty.map(({ name }) => name).join(", ")}
            </Ellipsis>
          </td>
        </tr>
      );
    });
  }

  render() {
    const { page } = this.state;
    const { isFetching, isAdmin, pageCount, userCount, userType } = this.props;

    // Non-admins shouldn't see user list
    if (!isAdmin) {
      return <div />;
    }

    const pagination = pageCount > 1 && (
      <Pagination
        total={pageCount}
        currentPage={page - 1}
        onPageSelected={this.onPageSelected}
      />
    );

    return (
      <div>
        <h2 className={styles.title}>
          {isFetching
            ? t("knowledge-user-segments.userSegment.matchingUsers")
            : t("knowledge-user-segments.userSegment.matchingUsersWithCount", {
                count: userCount,
              })}
        </h2>
        <p
          className={styles.description}
          dangerouslySetInnerHTML={{
            __html: t(
              "knowledge-user-segments.userSegment.matchingUsersDescription",
              { addUserUrl: "/agent/users/new" }
            ),
          }}
        />

        <table className={classNames(styles["c-table"], styles.table)}>
          <thead>
            <tr
              className={classNames(
                styles["c-table__row"],
                styles["c-table__row--header"]
              )}
            >
              <th
                className={classNames(
                  styles["c-table__row__cell"],
                  styles["c-table__row__cell--truncate"]
                )}
              >
                {t("knowledge-user-segments.userSegment.users")}
              </th>
              <th className={styles["c-table__row__cell"]}>
                {t("knowledge-user-segments.userSegment.email")}
              </th>
              <th className={styles["c-table__row__cell"]}>
                {t("knowledge-user-segments.userSegment.tags")}
              </th>

              {userType === STAFF ? (
                <th className={styles["c-table__row__cell"]}>
                  {t("knowledge-user-segments.userSegment.groups")}
                </th>
              ) : (
                <th className={styles["c-table__row__cell"]}>
                  {t("knowledge-user-segments.userSegment.organizations")}
                </th>
              )}
            </tr>
          </thead>
          <tbody>{this.renderRows()}</tbody>
        </table>

        {pagination}
      </div>
    );
  }
}

UserList.defaultProps = {
  users: [],
  isFetching: false,
};

UserList.propTypes = {
  users: PropTypes.array,
  isFetching: PropTypes.bool,
  isAdmin: PropTypes.bool,
  fetchUsers: PropTypes.func,
  userCount: PropTypes.number,
  pageCount: PropTypes.number,
  userType: PropTypes.oneOf([STAFF, SIGNED_IN_USERS]),
};

const mapStateToProps = ({ userSegment }, ownProps) => ({
  users: userSegment.users,
  userType: userSegment.userType,
  userCount: userSegment.userCount,
  pageCount: userSegment.userPageCount,
  isAdmin: hasEntitlement("admin"),
  isFetching:
    userSegment.isFetching ||
    userSegment.isFetchingUsers ||
    userSegment.isFetchingOrganizations ||
    userSegment.isFetchingGroups,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({ ...actions }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(UserList);



// WEBPACK FOOTER //
// ./src/components/UserSegment/components/UserList/index.js